import {Router} from '@angular/router';
import {BehaviorSubject,Observable,combineLatest,from,of,throwError,} from 'rxjs';
import {catchError,concatMap,mergeMap,shareReplay,tap,} from 'rxjs/operators';

import { createAuth0Client, Auth0Client } from '@auth0/auth0-spa-js';
import { Injectable, NgZone } from '@angular/core';
import { environment } from '../../environments/environment';

/*eslint-disable */

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _config;
  private _user = new BehaviorSubject<any>(null);
  get user() {
    return this._user.value;
  }
  user$ = this._user.asObservable();

  public routeFrom: string = "";
  public routeTo: string = "";
  auth0Client$: Observable<Auth0Client>;
  isAuthenticated$: Observable<any>;
  handleRedirectCallback$: Observable<any>;
  loggedIn: boolean = false;

  constructor(private router: Router, private ngZone: NgZone) {
    this._config = environment.alleAuthConfig;

    this.auth0Client$ = (from(
      createAuth0Client({
        domain: environment.alleAuthConfig.domain,
        clientId: environment.alleAuthConfig.clientID,
        authorizationParams: {
          audience: environment.alleAuthConfig.audience,
          redirect_uri: environment.alleAuthConfig.redirectUri,
        },
      })
    ) as Observable<Auth0Client>).pipe(
      shareReplay(1), // Every subscription receives the same shared value
      catchError((err) => throwError(err))
    );
    this.isAuthenticated$ = this.auth0Client$.pipe(
      concatMap((client: Auth0Client) => from(client.isAuthenticated())),
      tap((res) => {
        this.loggedIn = res;
      })
    );
    this.handleRedirectCallback$ = this.auth0Client$.pipe(
      concatMap((client: Auth0Client) => from(client.handleRedirectCallback()))
    );
  }

  getTokenSilently$(options?: Object): Observable<string> {
    return this.auth0Client$.pipe(
      concatMap((client: Auth0Client) => from(client.getTokenSilently(options)))
    );
  }

  getUser$(options?: Object): Observable<any> {
    return this.auth0Client$.pipe(
      concatMap((client: Auth0Client) => from(client.getUser())),
      tap((user) => {
        this.ngZone.run(() => {
          this._user.next(user);
        });
      })
    );
  }

  localAuthSetup() {
    // This should only be called on app initialization
    // Set up local authentication streams
    const checkAuth$ = this.isAuthenticated$.pipe(
      concatMap((loggedIn: boolean) => {
        if (loggedIn) {
          // If authenticated, get user and set in app
          // NOTE: you could pass options here if needed
          return this.getUser$();
        }
        // If not authenticated, return stream that emits 'false'
        return of(loggedIn);
      })
    );
    checkAuth$.subscribe();
  }

  login(redirectPath = '/notification') {
    // A desired redirect path can be passed to login method
    // (e.g., from a route guard)
    // Ensure Auth0 client instance exists
    this.auth0Client$.subscribe((client: Auth0Client) => {
      // Call method to log in
      client.loginWithRedirect({
        authorizationParams: {
          redirect_uri: this._config.redirectUri,
        },
        appState: { target: redirectPath },
      });
    });
  }

  handleAuthCallback() {
    // Call when app reloads after user logs in with Auth0
    const params = window.location.search;
    if (params.includes('code=') && params.includes('state=')) {
      let targetRoute: string; // Path to redirect to after login processsed
      const authComplete$ = this.handleRedirectCallback$.pipe(
        // Have client, now call method to handle auth callback redirect
        tap((cbRes: any) => {
          // Get and set target redirect route from callback results
          targetRoute =
            cbRes.appState && cbRes.appState.target
              ? cbRes.appState.target
              : '/';
        }),
        concatMap(() => {
          // Redirect callback complete; get user and login status
          return combineLatest([this.getUser$(), this.isAuthenticated$]);
        })
      );
      // Subscribe to authentication completion observable
      // Response will be an array of user and login status
      authComplete$.subscribe(([user, loggedIn]) => {
        // Redirect to target route after callback processing
        this.ngZone.run(() => {
          this.router.navigate([targetRoute]);
        });
      });
    }
  }

  logout() {
    // Ensure Auth0 client instance exists
    this.auth0Client$.subscribe((client: Auth0Client) => {
      // Call method to log out
      client.logout({
        clientId: this._config.clientID,
        logoutParams: {
          returnTo: this._config.logoutUrl
        }
      });
    });
  }

  getTokenSilently(): Observable<any> {
    return this.auth0Client$.pipe(
      mergeMap((client: Auth0Client) => {
        return from(
          client.getTokenSilently({
            authorizationParams: {
              audience: this._config.audience,
              scope: "",
              redirect_uri: this._config.redirectUri,
            }
          })
        ) as Observable<string>;
      })
    );
  }
  /*eslint-enable */
}
